import { buildTemplate, TEMPLATE_IDS, type TemplateId } from '@miroom/shared';
import { useMemo, useState } from 'react';
import { usePrefs } from '../../../application/stores/prefsStore';
import { MiniBoard } from '../../components/MiniBoard';
import { Modal } from '../../components/Modal';
import { Spinner } from '../../components/Spinner';
import { useT } from '../../i18n/i18n';
import styles from './Dashboard.module.css';

export function TemplateGallery({
  onPick,
  busy,
  ids = TEMPLATE_IDS,
  compact,
}: {
  onPick(id: TemplateId): void;
  busy: TemplateId | null;
  ids?: readonly TemplateId[];
  compact?: boolean;
}) {
  const t = useT();
  const locale = usePrefs((s) => s.locale);
  const previews = useMemo(
    () => Object.fromEntries(ids.map((id) => [id, buildTemplate(id, locale, 'preview').objects])),
    [ids, locale],
  );
  return (
    <ul className={compact ? styles.quickGrid : styles.templateGrid}>
      {ids.map((id) => (
        <li key={id}>
          <button
            type="button"
            className={styles.template}
            onClick={() => onPick(id)}
            disabled={busy !== null}
          >
            <span className={styles.templatePreview}>
              <MiniBoard objects={previews[id]!} />
              {busy === id && (
                <span className={styles.templateBusy}>
                  <Spinner size={22} label={t('templates.creating')} />
                </span>
              )}
            </span>
            <span className={styles.templateName}>{t(`templates.${id}.name`)}</span>
            {!compact && <span className={styles.templateDesc}>{t(`templates.${id}.desc`)}</span>}
          </button>
        </li>
      ))}
    </ul>
  );
}

export function TemplatesModal({
  onClose,
  onCreate,
}: {
  onClose(): void;
  onCreate(id: TemplateId): Promise<void>;
}) {
  const t = useT();
  const [busy, setBusy] = useState<TemplateId | null>(null);
  return (
    <Modal title={t('templates.title')} onClose={onClose} wide>
      <p className={styles.modalSubtitle}>{t('templates.subtitle')}</p>
      <TemplateGallery
        busy={busy}
        onPick={async (id) => {
          setBusy(id);
          try {
            await onCreate(id);
          } finally {
            setBusy(null);
          }
        }}
      />
    </Modal>
  );
}
